import { uGet, uSet } from "../core/storage";

// A lightweight research database for trading "filters" (metrics) observed across
// zone setups. The user keeps discovering new metrics (reach_wick_to_wick, body_%_avg…)
// and logs their values per setup + scenario, slowly building a filter profile for each.

export type Rating = "good" | "watch" | "avoid" | "none";

export interface Metric {
  id: string;
  label: string; // e.g. "reach_wick_to_wick"
}
export interface Setup {
  id: string;
  code: string; // e.g. "RBD"
  desc: string; // e.g. "reversal pattern & supply zone"
}
export interface Observation {
  id: string;
  setupId: string;
  name: string; // scenario, e.g. "3 legout candles"
  rating: Rating;
  values: Record<string, number | null>; // metricId -> value
  notes: string;
  createdAt: number;
}
export interface AnalysisDB {
  metrics: Metric[];
  setups: Setup[];
  observations: Observation[];
}

const KEY = "analysis.db";

export function newId(prefix = "a"): string {
  return `${prefix}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
}

function seed(): AnalysisDB {
  const m1: Metric = { id: newId("m"), label: "reach_wick_to_wick" };
  const m2: Metric = { id: newId("m"), label: "reach_wick_to_body" };
  const rbd: Setup = { id: newId("s"), code: "RBD", desc: "reversal pattern & supply zone" };
  const rbr: Setup = { id: newId("s"), code: "RBR", desc: "continuation & demand zone" };
  const dbr: Setup = { id: newId("s"), code: "DBR", desc: "reversal pattern & demand zone" };
  const dbd: Setup = { id: newId("s"), code: "DBD", desc: "continuation & supply zone" };
  return {
    metrics: [m1, m2],
    setups: [rbd, rbr, dbr, dbd],
    observations: [
      {
        id: newId("o"),
        setupId: rbd.id,
        name: "3 legout candles",
        rating: "good",
        values: { [m1.id]: 25.5, [m2.id]: 20 },
        notes: "Good so far — collecting more samples.",
        createdAt: Date.now(),
      },
      {
        id: newId("o"),
        setupId: rbd.id,
        name: "2 legout candles",
        rating: "watch",
        values: { [m1.id]: null, [m2.id]: null },
        notes: "Different data likely applies here — TBD.",
        createdAt: Date.now(),
      },
    ],
  };
}

export function loadDB(): AnalysisDB {
  const raw = uGet(KEY);
  if (!raw) {
    const db = seed();
    saveDB(db);
    return db;
  }
  try {
    const db = JSON.parse(raw) as AnalysisDB;
    db.metrics ||= [];
    db.setups ||= [];
    db.observations ||= [];
    return db;
  } catch {
    const db = seed();
    saveDB(db);
    return db;
  }
}

export function saveDB(db: AnalysisDB) {
  uSet(KEY, JSON.stringify(db));
}
